/**
 * Teacher assignment domain types, runtime guards, and fetch helpers.
 *
 * Module: lib/assignments
 * Layer:  util / data-access
 *
 * Purpose:
 *     Defines the teacher-facing shapes returned by the `/api/assignments/*`
 *     endpoints (the assignment list with submission/grading counts and the
 *     per-assignment submission roster), validates every response at the
 *     network boundary with hand-written runtime guards (the project does not
 *     declare zod), and exposes credentialed fetch helpers used by the
 *     teacher `/assignments` pages. Read helpers self-heal to an empty list or
 *     `null` on any non-OK response so the pages always render; the grade
 *     mutation returns the updated submission or `null` on failure.
 *
 * AI context:
 *     - Entry points called by: src/app/assignments/** pages and their hooks.
 *     - Depends on: the browser `fetch` API only (matches lib/quizzes).
 *     - Side effects: network I/O via the fetch helpers.
 *     - Invariants: a resolved value always passed its runtime guard; malformed
 *       rows are dropped, never coerced; read helpers never throw, so a 401/404
 *       from a not-yet-wired endpoint degrades gracefully.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - Responses are untrusted JSON. The `is*` guards are the trust boundary:
 *       only objects with the exact expected primitive shape survive, so the UI
 *       never renders attacker-controlled object keys.
 */

import type { TranslationKey } from './i18n';

/** The assignment categories the teacher UI renders as type badges. */
export type AssignmentType = 'homework' | 'project' | 'quiz' | 'essay' | 'lab' | 'other';

/** Lifecycle status of a teacher assignment. */
export type AssignmentStatus = 'draft' | 'published' | 'archived' | 'closed';

/** Lifecycle status of a single student submission. */
export type SubmissionStatus = 'not_submitted' | 'submitted' | 'graded' | 'returned';

/** One assignment row as shown to the teacher, with submission counts. */
export interface Assignment {
  readonly id: number;
  readonly title: string;
  readonly type: AssignmentType;
  readonly classroom_id: number;
  readonly classroom_name: string;
  readonly due_date: string | null;
  readonly points_possible: number;
  readonly status: AssignmentStatus;
  readonly submission_count: number;
  readonly graded_count: number;
  readonly created_at: string;
}

/** One student's submission to an assignment, as shown to the teacher. */
export interface AssignmentSubmission {
  readonly id: number;
  readonly assignment_id: number;
  readonly student_id: number;
  readonly student_name: string;
  readonly status: SubmissionStatus;
  readonly score: number | null;
  readonly submitted_at: string | null;
  readonly graded_at: string | null;
}

/** Assignment type -> i18n badge label key (data-driven). */
export const ASSIGNMENT_TYPE_KEY: Readonly<Record<AssignmentType, TranslationKey>> = {
  homework: 'assignments.type.homework',
  project: 'assignments.type.project',
  quiz: 'assignments.type.quiz',
  essay: 'assignments.type.essay',
  lab: 'assignments.type.lab',
  other: 'assignments.type.other',
};

/** Assignment status -> i18n badge label key (data-driven). */
export const ASSIGNMENT_STATUS_KEY: Readonly<Record<AssignmentStatus, TranslationKey>> = {
  draft: 'assignments.status.draft',
  published: 'assignments.status.published',
  archived: 'assignments.status.archived',
  closed: 'assignments.status.closed',
};

/** Submission status -> i18n badge label key (data-driven). */
export const SUBMISSION_STATUS_KEY: Readonly<Record<SubmissionStatus, TranslationKey>> = {
  not_submitted: 'assignments.submission.not_submitted',
  submitted: 'assignments.submission.submitted',
  graded: 'assignments.submission.graded',
  returned: 'assignments.submission.returned',
};

/** Optional filters for the assignment list endpoint. */
export interface AssignmentListParams {
  readonly classroomId?: number;
  readonly status?: AssignmentStatus;
  readonly search?: string;
}

/** The primitive types a guarded field may hold. */
type FieldType = 'string' | 'number' | 'boolean';

/** Narrow an unknown value to a non-null record. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Verify every entry of a field->type table against a record (data-driven).
 *
 * @param row - The candidate record.
 * @param fields - Field-name -> expected primitive type table.
 * @returns `true` when every listed field has its expected primitive type.
 */
function fieldsMatch(
  row: Record<string, unknown>,
  fields: Readonly<Record<string, FieldType>>,
): boolean {
  return Object.entries(fields).every(([name, expected]) => typeof row[name] === expected);
}

/**
 * True when `value` is `null` or matches `expected` primitive type.
 *
 * @param value - The candidate field value.
 * @param expected - The non-null primitive type to accept.
 * @returns `true` for `null` or a matching primitive.
 */
function isNullOr(value: unknown, expected: FieldType): boolean {
  return value === null || typeof value === expected;
}

/** The set of valid assignment-type discriminants (membership table). */
const ASSIGNMENT_TYPES: ReadonlySet<AssignmentType> = new Set<AssignmentType>([
  'homework',
  'project',
  'quiz',
  'essay',
  'lab',
  'other',
]);

/** The set of valid assignment-status discriminants (membership table). */
const ASSIGNMENT_STATUSES: ReadonlySet<AssignmentStatus> = new Set<AssignmentStatus>([
  'draft',
  'published',
  'archived',
  'closed',
]);

/** The set of valid submission-status discriminants (membership table). */
const SUBMISSION_STATUSES: ReadonlySet<SubmissionStatus> = new Set<SubmissionStatus>([
  'not_submitted',
  'submitted',
  'graded',
  'returned',
]);

/** Required primitive fields for {@link Assignment} (nullable/enum fields checked separately). */
const ASSIGNMENT_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  title: 'string',
  classroom_id: 'number',
  classroom_name: 'string',
  points_possible: 'number',
  submission_count: 'number',
  graded_count: 'number',
  created_at: 'string',
};

/** Required primitive fields for {@link AssignmentSubmission} (nullable/enum fields checked separately). */
const SUBMISSION_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  assignment_id: 'number',
  student_id: 'number',
  student_name: 'string',
};

/** Type guard for the {@link AssignmentType} discriminant. */
function isAssignmentType(value: unknown): value is AssignmentType {
  return typeof value === 'string' && ASSIGNMENT_TYPES.has(value as AssignmentType);
}

/** Type guard for the {@link AssignmentStatus} discriminant. */
function isAssignmentStatus(value: unknown): value is AssignmentStatus {
  return typeof value === 'string' && ASSIGNMENT_STATUSES.has(value as AssignmentStatus);
}

/** Type guard for the {@link SubmissionStatus} discriminant. */
function isSubmissionStatus(value: unknown): value is SubmissionStatus {
  return typeof value === 'string' && SUBMISSION_STATUSES.has(value as SubmissionStatus);
}

/** Type guard for {@link Assignment}. */
export function isAssignment(value: unknown): value is Assignment {
  const row = asRecord(value);
  if (row === null || !fieldsMatch(row, ASSIGNMENT_FIELDS)) {
    return false;
  }
  return (
    isNullOr(row['due_date'], 'string') &&
    isAssignmentType(row['type']) &&
    isAssignmentStatus(row['status'])
  );
}

/** True when a submission record's nullable timestamp/score fields are well-typed. */
function submissionNullablesOk(row: Record<string, unknown>): boolean {
  return (
    isNullOr(row['score'], 'number') &&
    isNullOr(row['submitted_at'], 'string') &&
    isNullOr(row['graded_at'], 'string')
  );
}

/** Type guard for {@link AssignmentSubmission}. */
export function isAssignmentSubmission(value: unknown): value is AssignmentSubmission {
  const row = asRecord(value);
  if (row === null || !fieldsMatch(row, SUBMISSION_FIELDS)) {
    return false;
  }
  return submissionNullablesOk(row) && isSubmissionStatus(row['status']);
}

/** Build a credentialed GET init, optionally cancellable. */
function getInit(signal?: AbortSignal): RequestInit {
  return signal ? { credentials: 'include', signal } : { credentials: 'include' };
}

/** Stringify the classroom id filter, or empty when absent. */
function classroomIdParam(params?: AssignmentListParams): string {
  return params?.classroomId === undefined ? '' : String(params.classroomId);
}

/** Build the (param-name -> raw value) filter table, including empties. */
function listFilterEntries(
  params?: AssignmentListParams,
): ReadonlyArray<readonly [string, string]> {
  return [
    ['classroom_id', classroomIdParam(params)],
    ['status', params?.status ?? ''],
    ['search', params?.search ?? ''],
  ];
}

/** Build the assignment-list query string from optional filters. */
function buildListQuery(params?: AssignmentListParams): string {
  const search = new URLSearchParams();
  const present = listFilterEntries(params).filter(([, raw]) => raw !== '');
  for (const [name, raw] of present) {
    search.set(name, raw);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch and validate the teacher's assignments, optionally filtered.
 *
 * @param params - Optional classroom / status / search filters.
 * @param signal - Optional abort signal so callers can cancel in-flight loads.
 * @returns The validated assignments (malformed rows dropped); `[]` on any
 *   non-OK response or malformed body.
 * @remarks Sends the opaque session cookie via `credentials: 'include'`; never
 *   throws, so a 401/404 from a not-yet-built endpoint degrades gracefully.
 */
export async function fetchAssignments(
  params?: AssignmentListParams,
  signal?: AbortSignal,
): Promise<readonly Assignment[]> {
  const res = await fetch(`/api/assignments${buildListQuery(params)}`, getInit(signal));
  if (!res.ok) {
    return [];
  }
  const body: unknown = await res.json();
  return Array.isArray(body) ? body.filter(isAssignment) : [];
}

/**
 * Fetch and validate one assignment by id.
 *
 * @param assignmentId - The assignment to load.
 * @param signal - Optional abort signal.
 * @returns The validated {@link Assignment}, or `null` on any failure.
 * @remarks Never throws on HTTP/parse errors so callers can render not-found.
 */
export async function fetchAssignment(
  assignmentId: number,
  signal?: AbortSignal,
): Promise<Assignment | null> {
  const res = await fetch(`/api/assignments/${String(assignmentId)}`, getInit(signal));
  if (!res.ok) {
    return null;
  }
  const body: unknown = await res.json();
  return isAssignment(body) ? body : null;
}

/**
 * Fetch and validate the submission roster for an assignment.
 *
 * @param assignmentId - The assignment whose submissions to load.
 * @param signal - Optional abort signal.
 * @returns The validated submissions (malformed rows dropped); `[]` on failure.
 * @remarks Never throws so the detail page can render an empty roster.
 */
export async function fetchAssignmentSubmissions(
  assignmentId: number,
  signal?: AbortSignal,
): Promise<readonly AssignmentSubmission[]> {
  const path = `/api/assignments/${String(assignmentId)}/submissions`;
  const res = await fetch(path, getInit(signal));
  if (!res.ok) {
    return [];
  }
  const body: unknown = await res.json();
  return Array.isArray(body) ? body.filter(isAssignmentSubmission) : [];
}

/**
 * Grade a single submission with a score and optional feedback.
 *
 * @param submissionId - The submission being graded.
 * @param score - The non-negative score to record.
 * @param feedback - Optional teacher feedback shown to the student.
 * @param signal - Optional abort signal.
 * @returns The validated, updated {@link AssignmentSubmission}, or `null` on failure.
 * @remarks PUTs the grade; the backend records it and returns the updated row.
 *   Never throws on HTTP/parse errors so callers can show a save-error state.
 */
export async function gradeSubmission(
  submissionId: number,
  score: number,
  feedback: string,
  signal?: AbortSignal,
): Promise<AssignmentSubmission | null> {
  const init: RequestInit = {
    ...getInit(signal),
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ score, feedback }),
  };
  const res = await fetch(`/api/assignments/submissions/${String(submissionId)}/grade`, init);
  if (!res.ok) {
    return null;
  }
  const body: unknown = await res.json();
  return isAssignmentSubmission(body) ? body : null;
}
